import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import { useFlyingCart } from '../contexts/FlyingCartContext';
import { useSound } from '../hooks/useSound';
import { productService } from '../services';
import { gtagService } from '../services/gtagService';
import type { ProductMedia, Product } from '../types/Product';

const ProductDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart, updateQuantity, getItemQuantity } = useCart();
  const { addToFavorites, removeFromFavorites, isFavorite } = useUser();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const { triggerFlyingAnimation } = useFlyingCart();
  const { play: playKissSound } = useSound('/sounds/kiss.mp3', { volume: 0.9 });
  
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [fullscreenMediaIndex, setFullscreenMediaIndex] = useState(0);

  // Icon Components
  const HeartIcon = ({ className = "w-5 h-5", filled = false }: { className?: string; filled?: boolean }) => (
    <svg className={className} fill={filled ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

  const CartIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  );

  const PackageIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );

  const ShieldIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const TruckIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );

  const ShareIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.632 6.316a3 3 0 11-5.368-2.684m5.368 2.684a3 3 0 01-5.368-2.684m0 0a3 3 0 110-5.368m0 5.368L8.684 10.658" />
    </svg>
  );

  const StarIcon = ({ className = "w-4 h-4", filled = false }: { className?: string; filled?: boolean }) => (
    <svg className={`${className} ${filled ? 'fill-current' : ''}`} viewBox="0 0 24 24">
      <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
    </svg>
  );

  const CheckCircleIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const XIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const VideoIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );

  const ExpandIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  );

  const ChevronLeftIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );

  const ChevronRightIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string) => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Get media array
  const getMediaArray = (): ProductMedia[] => {
    if (!product) return [];
    
    if (product.medias && product.medias.length > 0) {
      return product.medias;
    }
    
    // Fallback: if no medias but has image field
    if (product.image) {
      return [{ 
        type: 'image', 
        url: product.image,
        displayOrder: 0,
        isMain: true
      }];
    }
    
    return [];
  };

  // Handle fullscreen open
  const handleOpenFullscreen = useCallback(() => {
    setFullscreenMediaIndex(selectedMediaIndex);
    setShowFullscreen(true);
    document.body.style.overflow = 'hidden';
  }, [selectedMediaIndex]);
  
  // Handle fullscreen close
  const handleCloseFullscreen = useCallback(() => {
    setShowFullscreen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Navigate media in fullscreen
  const navigateFullscreenMedia = useCallback((direction: 'prev' | 'next') => {
    setFullscreenMediaIndex((currentIndex) => {
      const mediaArray = getMediaArray();
      if (mediaArray.length === 0) return currentIndex;
      
      const newIndex = direction === 'prev' 
        ? (currentIndex - 1 + mediaArray.length) % mediaArray.length
        : (currentIndex + 1) % mediaArray.length;
      
      // Also update the main gallery
      setSelectedMediaIndex(newIndex);
      return newIndex;
    });
  }, [product]);

  // Load product data
  useEffect(() => {
    const loadProductData = async () => {
      if (!slug) return;
      
      try {
        // Load product details by slug
        const productResponse = await productService.getProductBySlug(slug);
        
        if (productResponse.success && productResponse.data) {
          setProduct(productResponse.data);
          
          // Track product view
          gtagService.trackProductView(productResponse.data);
          
          // Load related products
          const relatedResponse = await productService.getRelatedProducts(productResponse.data.id, 4);
          if (relatedResponse.success) {
            setRelatedProducts(relatedResponse.data);
          }
        } else {
          showToast('Product not found', 'error');
          navigate('/products');
        }
      } catch {
        console.error('Error occurred');
        showToast('Failed to load product', 'error');
        navigate('/products');
      }
    };

    loadProductData();
  }, [slug, navigate, showToast]);

  // Handle keyboard navigation for fullscreen
  useEffect(() => {
    if (!showFullscreen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        navigateFullscreenMedia('prev');
      } else if (e.key === 'ArrowRight') {
        navigateFullscreenMedia('next');
      } else if (e.key === 'Escape') {
        handleCloseFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showFullscreen, navigateFullscreenMedia, handleCloseFullscreen]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  const cartQuantity = getItemQuantity(product.id);
  const discountPercentage = product.originalPrice 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleAddToCart = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!product.inStock) {
      showToast('This product is out of stock', 'error');
      return;
    }
    
    // Get button position for flying animation
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const startPosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
      triggerFlyingAnimation(startPosition, product.image);
    }
    
    // Add the product to cart (just 1 item)
    addToCart(product);
    
    // Track add to cart event
    gtagService.trackAddToCart(product, 1);
    
    showToast(`${product.name} added to cart!`, 'success');
  };

  const handleQuantityChange = (newQuantity: number, element?: HTMLElement) => {
    if (newQuantity < 0) return;
    
    // If item is already in cart, directly update the cart quantity
    if (cartQuantity > 0) {
      if (newQuantity === 0) {
        updateQuantity(product.id, 0);
        // Track remove from cart
        gtagService.trackRemoveFromCart(product, cartQuantity);
        showToast('Removed from cart', 'success');
      } else {
        // Trigger animation if quantity is increasing
        if (newQuantity > cartQuantity && element) {
          const rect = element.getBoundingClientRect();
          const startPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
          triggerFlyingAnimation(startPosition, product.image);
          // Track add to cart for the quantity difference
          gtagService.trackAddToCart(product, newQuantity - cartQuantity);
        } else if (newQuantity < cartQuantity) {
          // Track remove from cart for the quantity difference
          gtagService.trackRemoveFromCart(product, cartQuantity - newQuantity);
        }
        updateQuantity(product.id, newQuantity);
      }
    }
  };

  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/product/${product.id}` } });
      return;
    }
    if (isFavorite(product.id)) {
      removeFromFavorites(product.id);
      showToast('Removed from favorites', 'success');
    } else {
      addToFavorites(product);
      playKissSound(); // Play kiss sound when adding to favorites
      showToast('Added to favorites!', 'success');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Check out ${product.name} at Demo Crackers!`,
          url: window.location.href
        });
      } catch {
        console.error('Error occurred');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      showToast('Link copied to clipboard!', 'success');
    }
  };


  const mediaArray = getMediaArray();
  const currentMedia = mediaArray[selectedMediaIndex] || mediaArray[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <nav className="flex items-center space-x-2 text-sm">
          <button onClick={() => navigate('/')} className="text-neutral-500 hover:text-accent-600">
            Home
          </button>
          <span className="text-neutral-400">/</span>
          <button onClick={() => navigate('/products')} className="text-neutral-500 hover:text-accent-600">
            Products
          </button>
          <span className="text-neutral-400">/</span>
          <span className="text-neutral-900 font-medium">{product.name}</span>
        </nav>
      </div>

      <div className="container mx-auto px-4 pb-16">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-8">
            {/* Product Media Gallery */}
            <div className="space-y-4">
              <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group cursor-pointer">
                {currentMedia && currentMedia.type === 'video' ? (
                  <div className="w-full h-full relative" onClick={handleOpenFullscreen}>
                    <iframe
                      className="w-full h-full pointer-events-none"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentMedia.url)}`}
                      title={`${product.name} video`}
                      style={{ border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                      <ExpandIcon className="w-12 h-12 text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="relative w-full h-full" onClick={handleOpenFullscreen}>
                    <img
                      src={currentMedia?.url || product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <ExpandIcon className="w-12 h-12 text-white" />
                    </div>
                  </div>
                )}
                {currentMedia?.type !== 'video' && (
                  <>
                    {discountPercentage > 0 && (
                      <div className="absolute top-4 left-4 bg-danger text-white px-3 py-1 rounded-full text-sm font-semibold">
                        -{discountPercentage}%
                      </div>
                    )}
                    {product.isNew && (
                      <div className="absolute top-4 right-4 bg-success text-white px-3 py-1 rounded-full text-sm font-semibold">
                        New
                      </div>
                    )}
                    {!product.inStock && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className="bg-white text-black px-4 py-2 rounded-lg font-semibold">
                          Out of Stock
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Media Thumbnails */}
              <div className="grid grid-cols-5 gap-2">
                {mediaArray.map((media, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedMediaIndex(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all relative ${
                      selectedMediaIndex === index ? 'border-accent-600' : 'border-gray-200'
                    }`}
                  >
                    {media.type === 'video' ? (
                      <>
                        <img 
                          src={`https://img.youtube.com/vi/${getYouTubeVideoId(media.url)}/hqdefault.jpg`} 
                          alt={`${product.name} video`} 
                          className="w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                          <div className="bg-white bg-opacity-90 rounded-full p-1.5">
                            <VideoIcon className="w-4 h-4 text-accent-600" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <img 
                        src={media.url} 
                        alt={`${product.name} ${index + 1}`} 
                        className="w-full h-full object-cover" 
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-neutral-900 mb-2">{product.name}</h1>
                <div className="flex items-center space-x-4 text-sm">
                  {/* Show rating only if product has reviews */}
                  {product.rating && product.reviewCount && product.reviewCount > 0 ? (
                    <>
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon key={i} className={`w-4 h-4 ${i < Math.floor(product.rating || 0) ? 'text-warning' : 'text-gray-300'}`} filled={i < Math.floor(product.rating || 0)} />
                        ))}
                        <span className="ml-2 text-neutral-600">{product.rating?.toFixed(1)} ({product.reviewCount} reviews)</span>
                      </div>
                      <span className="text-neutral-400">|</span>
                    </>
                  ) : null}
                  <span className="text-neutral-600">Category: {product.category.replace('-', ' ')}</span>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-end space-x-3">
                <span className="text-4xl font-bold text-accent-600">₹{product.price}</span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-xl text-neutral-400 line-through">₹{product.originalPrice}</span>
                )}
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-gray-200">
                <div className="flex items-center space-x-2">
                  <TruckIcon className="w-5 h-5 text-accent-600" />
                  <span className="text-sm">Lorry Service Delivery</span>
                </div>
                <div className="flex items-center space-x-2">
                  <ShieldIcon className="w-5 h-5 text-accent-600" />
                  <span className="text-sm">Safe & Certified</span>
                </div>
                <div className="flex items-center space-x-2">
                  <PackageIcon className="w-5 h-5 text-accent-600" />
                  <span className="text-sm">Secure Packaging</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="w-5 h-5 text-accent-600" />
                  <span className="text-sm">Quality Assured</span>
                </div>
              </div>

              {/* Add to Cart Section */}
              <div className="space-y-4">
                {/* Main Action Button */}
                <div>
                  {cartQuantity > 0 ? (
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center border border-gray-300 rounded-lg">
                        <button
                          onClick={() => handleQuantityChange(cartQuantity - 1)}
                          className="p-3 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-xl">-</span>
                        </button>
                        <span className="px-6 font-semibold">{cartQuantity}</span>
                        <button
                          onClick={(e) => handleQuantityChange(cartQuantity + 1, e.currentTarget)}
                          className="p-3 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-xl">+</span>
                        </button>
                      </div>
                      <button
                        onClick={() => navigate('/cart')}
                        className="flex-1 bg-accent-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-accent-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <CartIcon className="w-5 h-5" />
                        <span>Go to Cart</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => handleAddToCart(e)}
                      disabled={!product.inStock}
                      className="w-full bg-accent-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-accent-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      <CartIcon className="w-5 h-5" />
                      <span>Add to Cart</span>
                    </button>
                  )}
                </div>

                {/* Secondary Actions */}
                <div className="flex space-x-4">
                  <button
                    onClick={handleToggleFavorite}
                    className={`flex-1 border-2 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 ${
                      isFavorite(product.id)
                        ? 'border-danger text-danger bg-danger bg-opacity-10'
                        : 'border-gray-300 text-neutral-700 hover:border-danger hover:text-danger'
                    }`}
                  >
                    <HeartIcon className="w-5 h-5" filled={isFavorite(product.id)} />
                    <span>{isFavorite(product.id) ? 'Saved' : 'Save'}</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex-1 border-2 border-gray-300 text-neutral-700 py-3 px-6 rounded-lg font-semibold hover:border-accent-600 hover:text-accent-600 transition-all flex items-center justify-center space-x-2"
                  >
                    <ShareIcon className="w-5 h-5" />
                    <span>Share</span>
                  </button>
                </div>
              </div>

              {/* Stock Status */}
              {product.inStock ? (
                <div className="flex items-center space-x-2 text-success">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span className="font-medium">In Stock</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-danger">
                  <XIcon className="w-5 h-5" />
                  <span className="font-medium">Out of Stock</span>
                </div>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <div className="border-t border-gray-200">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="flex space-x-8 border-b border-gray-200">
                {['description', 'specifications', 'reviews', 'safety'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-2 font-medium capitalize transition-all relative ${
                      activeTab === tab
                        ? 'text-accent-600 border-b-2 border-accent-600'
                        : 'text-neutral-600 hover:text-accent-600'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="py-8">
                {activeTab === 'description' && (
                  <div className="prose max-w-none">
                    <p className="text-neutral-700 leading-relaxed">
                      {product.description || 'No description available for this product.'}
                    </p>
                    {product.tags && product.tags.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">Tags:</h3>
                        <div className="flex flex-wrap gap-2">
                          {product.tags.map((tag, index) => (
                            <span key={index} className="px-3 py-1 bg-gray-100 text-neutral-700 rounded-full text-sm">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'specifications' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-3">Product Details</h3>
                      <dl className="space-y-2">
                        {product.brand && (
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-neutral-600">Brand</dt>
                            <dd className="font-medium">{product.brand}</dd>
                          </div>
                        )}
                        <div className="flex justify-between py-2 border-b border-gray-100">
                          <dt className="text-neutral-600">Category</dt>
                          <dd className="font-medium capitalize">{product.category.replace('-', ' ')}</dd>
                        </div>
                        {product.productCode && (
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-neutral-600">Product Code</dt>
                            <dd className="font-medium">{product.productCode}</dd>
                          </div>
                        )}
                        {product.packSize && (
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-neutral-600">Package Type</dt>
                            <dd className="font-medium">{product.packSize}</dd>
                          </div>
                        )}
                        {product.piecesPerBox && (
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-neutral-600">Pieces per Box</dt>
                            <dd className="font-medium">{product.piecesPerBox}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">General Information</h3>
                      <dl className="space-y-2">
                        <div className="flex justify-between py-2 border-b border-gray-100">
                          <dt className="text-neutral-600">Stock Status</dt>
                          <dd className="font-medium">{product.inStock ? 'In Stock' : 'Out of Stock'}</dd>
                        </div>
                        {product.discount && product.discount > 0 && (
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <dt className="text-neutral-600">Discount</dt>
                            <dd className="font-medium">{product.discount}% OFF</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <div className="text-center py-12">
                      <p className="text-lg text-neutral-600">No reviews yet.</p>
                      <p className="text-sm text-neutral-500 mt-2">Be the first to review this product!</p>
                    </div>
                  </div>
                )}

                {activeTab === 'safety' && (
                  <div className="prose max-w-none">
                    <h3 className="text-xl font-semibold mb-4">Safety Guidelines</h3>
                    <div className="bg-warning bg-opacity-10 border border-warning rounded-lg p-4">
                      <p className="text-warning font-semibold mb-2">⚠️ Important Safety Information</p>
                      <p className="text-sm text-neutral-700 mb-4">
                        Fireworks should only be used by adults. Keep away from children. Always follow the instructions on the product label.
                      </p>
                      <ul className="space-y-3 text-neutral-700">
                        <li>• Read all instructions before use</li>
                        <li>• Light only one firework at a time</li>
                        <li>• Never attempt to relight a "dud" firework</li>
                        <li>• Always have water or a fire extinguisher nearby</li>
                        <li>• Light fireworks outdoors in a clear area away from buildings and vehicles</li>
                        <li>• Never carry fireworks in your pocket</li>
                        <li>• Store in a cool, dry place away from heat sources</li>
                        <li>• Dispose of used fireworks by soaking in water before throwing away</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  onClick={() => navigate(`/product/${relatedProduct.slug}`)}
                  className="bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square bg-gray-100">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-neutral-900 line-clamp-2">{relatedProduct.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-lg font-bold text-accent-600">₹{relatedProduct.price}</span>
                      {relatedProduct.originalPrice && relatedProduct.originalPrice > relatedProduct.price && (
                        <span className="text-sm text-neutral-400 line-through">
                          ₹{relatedProduct.originalPrice}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {showFullscreen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
          <button
            onClick={handleCloseFullscreen}
            className="absolute top-4 right-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all z-10"
          >
            <XIcon className="w-8 h-8 text-white" />
          </button>

          <div className="relative w-full h-full flex items-center justify-center px-4">
            {mediaArray.length > 1 && (
              <>
                {/* Previous Media Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullscreenMedia('prev');
                  }}
                  className="absolute left-4 p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all z-50"
                  aria-label="Previous media"
                >
                  <ChevronLeftIcon className="w-8 h-8 text-white" />
                </button>

                {/* Next Media Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateFullscreenMedia('next');
                  }}
                  className="absolute right-4 p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all z-50"
                  aria-label="Next media"
                >
                  <ChevronRightIcon className="w-8 h-8 text-white" />
                </button>
              </>
            )}

            {/* Fullscreen Media Display */}
            {mediaArray[fullscreenMediaIndex]?.type === 'video' ? (
              <div className="relative w-full h-full max-w-7xl mx-auto">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(mediaArray[fullscreenMediaIndex].url)}`}
                  title={`${product.name} video`}
                  style={{ border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
            ) : (
              <img
                src={mediaArray[fullscreenMediaIndex]?.url || product.image}
                alt={product.name}
                className="max-w-full max-h-full object-contain"
              />
            )}

            {/* Media Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-20 px-4 py-2 rounded-full">
              <span className="text-white font-medium flex items-center gap-2">
                {mediaArray[fullscreenMediaIndex]?.type === 'video' && (
                  <VideoIcon className="w-4 h-4" />
                )}
                {fullscreenMediaIndex + 1} / {mediaArray.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;